/**
 * Chart Annotation Utilities
 *
 * Generates ApexCharts annotations for deployment periods and events.
 * Used to visualize deployment boundaries and milestones on energy charts.
 */

import type { ApexOptions } from 'apexcharts';
import type { DeploymentPeriod, UnitEvent, UnitEventType } from '@/types/history';

/**
 * ApexCharts xaxis annotation type
 */
interface XAxisAnnotation {
  x?: number;
  x2?: number;
  fillColor?: string;
  opacity?: number;
  borderColor?: string;
  borderWidth?: number;
  strokeDashArray?: number;
  label?: {
    text?: string;
    position?: string;
    style?: {
      color?: string;
      background?: string;
      fontSize?: string;
      fontWeight?: string | number;
    };
    offsetX?: number;
    offsetY?: number;
  };
}

/**
 * ApexCharts point annotation type
 */
interface PointAnnotation {
  x: number;
  y?: number;
  marker?: {
    size?: number;
    fillColor?: string;
    strokeColor?: string;
    strokeWidth?: number;
    shape?: string;
    radius?: number;
  };
  label?: {
    text?: string;
    borderColor?: string;
    style?: {
      color?: string;
      background?: string;
      fontSize?: string;
    };
    offsetY?: number;
  };
}

/**
 * Color palette for deployment regions
 */
const DEPLOYMENT_COLORS = {
  internal: '#f3f4f6', // gray-100 - Hollywood Trucks internal
  external: '#dbeafe', // blue-100 - External client
  boundary: '#6b7280', // gray-500 - Deployment boundary line
};

/**
 * Generate xaxis annotations for deployment periods
 *
 * Creates shaded regions for each deployment period and dashed vertical
 * lines at deployment boundaries.
 */
export function generateDeploymentAnnotations(
  deployments: DeploymentPeriod[],
  chartStartDate: Date,
  chartEndDate: Date
): XAxisAnnotation[] {
  const annotations: XAxisAnnotation[] = [];
  const chartStartMs = chartStartDate.getTime();
  const chartEndMs = chartEndDate.getTime();

  for (const deployment of deployments) {
    const deployStart = new Date(deployment.started_at).getTime();
    const deployEnd = deployment.ended_at
      ? new Date(deployment.ended_at).getTime()
      : chartEndMs;

    // Skip if deployment doesn't overlap with chart range
    if (deployEnd < chartStartMs || deployStart > chartEndMs) {
      continue;
    }

    // Clamp to chart range
    const visibleStart = Math.max(deployStart, chartStartMs);
    const visibleEnd = Math.min(deployEnd, chartEndMs);

    // Shaded region for deployment period
    annotations.push({
      x: visibleStart,
      x2: visibleEnd,
      fillColor: deployment.is_internal
        ? DEPLOYMENT_COLORS.internal
        : DEPLOYMENT_COLORS.external,
      opacity: 0.3,
      label: {
        text: deployment.company_name || 'Unknown',
        position: 'top',
        style: {
          color: '#374151',
          background: 'transparent',
          fontSize: '11px',
          fontWeight: 500,
        },
        offsetY: -5,
      },
    });

    // Dashed vertical line at deployment start (if visible)
    if (deployStart >= chartStartMs && deployStart <= chartEndMs) {
      annotations.push({
        x: deployStart,
        borderColor: DEPLOYMENT_COLORS.boundary,
        borderWidth: 1,
        strokeDashArray: 4,
        label: {
          text: 'Deployed',
          position: 'top',
          style: {
            color: '#6b7280',
            background: '#ffffff',
            fontSize: '10px',
          },
          offsetY: -20,
        },
      });
    }

    // Dashed vertical line at deployment end (if not current and visible)
    if (
      deployment.ended_at &&
      deployEnd >= chartStartMs &&
      deployEnd <= chartEndMs
    ) {
      annotations.push({
        x: deployEnd,
        borderColor: DEPLOYMENT_COLORS.boundary,
        borderWidth: 1,
        strokeDashArray: 4,
        label: {
          text: 'Returned',
          position: 'top',
          style: {
            color: '#6b7280',
            background: '#ffffff',
            fontSize: '10px',
          },
          offsetY: -20,
        },
      });
    }
  }

  return annotations;
}

/**
 * Event type colors for point annotations
 */
const EVENT_COLORS: Partial<Record<UnitEventType, string>> = {
  first_telemetry_starlink: '#3B82F6',
  first_telemetry_solark: '#F59E0B',
  first_telemetry_hvac: '#06B6D4',
  first_telemetry_tesla: '#EC4899',
  first_telemetry_aqi: '#10B981',
  first_telemetry_gps: '#8B5CF6',
  unit_created: '#8FB560',
  system_installed: '#22C55E',
  maintenance_started: '#F97316',
  maintenance_completed: '#22C55E',
};

/**
 * Event type labels for point annotations
 */
const EVENT_LABELS: Partial<Record<UnitEventType, string>> = {
  first_telemetry_starlink: 'Starlink',
  first_telemetry_solark: 'Sol-Ark',
  first_telemetry_hvac: 'HVAC',
  first_telemetry_tesla: 'Tesla',
  first_telemetry_aqi: 'AQI',
  first_telemetry_gps: 'GPS',
  unit_created: 'Created',
  system_installed: 'Installed',
  maintenance_started: 'Maint.',
  maintenance_completed: 'Done',
};

/**
 * Generate point annotations for milestone events
 *
 * Creates circular markers on the chart for significant events
 * like first telemetry, system installations, etc.
 */
export function generateEventAnnotations(
  events: UnitEvent[],
  chartStartDate: Date,
  chartEndDate: Date
): PointAnnotation[] {
  const annotations: PointAnnotation[] = [];
  const chartStartMs = chartStartDate.getTime();
  const chartEndMs = chartEndDate.getTime();

  // Filter to milestone events we want to show
  const milestoneTypes: UnitEventType[] = [
    'first_telemetry_starlink',
    'first_telemetry_solark',
    'first_telemetry_hvac',
    'first_telemetry_tesla',
    'first_telemetry_aqi',
    'first_telemetry_gps',
    'unit_created',
    'system_installed',
    'maintenance_started',
    'maintenance_completed',
  ];

  const milestoneEvents = events.filter((e) =>
    milestoneTypes.includes(e.event_type)
  );

  for (const event of milestoneEvents) {
    const eventTime = new Date(event.event_date).getTime();

    // Skip if outside chart range
    if (eventTime < chartStartMs || eventTime > chartEndMs) {
      continue;
    }

    const color = EVENT_COLORS[event.event_type] || '#6b7280';
    const label = EVENT_LABELS[event.event_type] || event.event_type;

    annotations.push({
      x: eventTime,
      marker: {
        size: 6,
        fillColor: color,
        strokeColor: '#ffffff',
        strokeWidth: 2,
        shape: 'circle',
      },
      label: {
        text: label,
        borderColor: color,
        style: {
          color: '#ffffff',
          background: color,
          fontSize: '10px',
        },
        offsetY: -10,
      },
    });
  }

  return annotations;
}

/**
 * Merge annotations into ApexCharts options
 *
 * Combines deployment regions and event markers into the chart's
 * annotation configuration.
 */
export function mergeAnnotations(
  baseOptions: ApexOptions,
  deployments: DeploymentPeriod[],
  events: UnitEvent[],
  chartStartDate: Date,
  chartEndDate: Date,
  options?: {
    showDeployments?: boolean;
    showEvents?: boolean;
  }
): ApexOptions {
  const { showDeployments = true, showEvents = true } = options || {};

  const xaxisAnnotations: XAxisAnnotation[] = [];
  const pointAnnotations: PointAnnotation[] = [];

  if (showDeployments && deployments.length > 0) {
    xaxisAnnotations.push(
      ...generateDeploymentAnnotations(deployments, chartStartDate, chartEndDate)
    );
  }

  if (showEvents && events.length > 0) {
    pointAnnotations.push(
      ...generateEventAnnotations(events, chartStartDate, chartEndDate)
    );
  }

  return {
    ...baseOptions,
    annotations: {
      ...baseOptions.annotations,
      xaxis: [
        ...(baseOptions.annotations?.xaxis || []),
        ...xaxisAnnotations,
      ],
      points: [
        ...(baseOptions.annotations?.points || []),
        ...pointAnnotations,
      ],
    },
  };
}

/**
 * Filter chart data by deployment visibility rules
 *
 * For external users, only returns data points that fall within
 * their deployment period(s).
 */
export function filterDataByDeployments<T extends { date: string | Date }>(
  data: T[],
  deployments: DeploymentPeriod[],
  isInternalUser: boolean
): T[] {
  // Internal users see all data
  if (isInternalUser) {
    return data;
  }

  // External users only see data from their deployment periods
  return data.filter((item) => {
    const itemDate = new Date(item.date);
    return deployments.some((d) => {
      const start = new Date(d.started_at);
      const end = d.ended_at ? new Date(d.ended_at) : new Date();
      return itemDate >= start && itemDate < end;
    });
  });
}

/**
 * Get the visible date range based on deployment visibility
 *
 * For external users, restricts the chart date range to their
 * deployment periods.
 */
export function getVisibleDateRange(
  requestedStart: Date,
  requestedEnd: Date,
  deployments: DeploymentPeriod[],
  isInternalUser: boolean
): { start: Date; end: Date } {
  // Internal users see the full requested range
  if (isInternalUser || deployments.length === 0) {
    return { start: requestedStart, end: requestedEnd };
  }

  // External users - find the intersection of deployments with requested range
  let visibleStart = requestedEnd;
  let visibleEnd = requestedStart;

  for (const deployment of deployments) {
    const deployStart = new Date(deployment.started_at);
    const deployEnd = deployment.ended_at
      ? new Date(deployment.ended_at)
      : new Date();

    // Skip if deployment doesn't overlap with requested range
    if (deployEnd < requestedStart || deployStart > requestedEnd) {
      continue;
    }

    // Expand visible range to include this deployment
    const overlapStart = new Date(
      Math.max(deployStart.getTime(), requestedStart.getTime())
    );
    const overlapEnd = new Date(
      Math.min(deployEnd.getTime(), requestedEnd.getTime())
    );

    if (overlapStart < visibleStart) {
      visibleStart = overlapStart;
    }
    if (overlapEnd > visibleEnd) {
      visibleEnd = overlapEnd;
    }
  }

  // If no overlap found, return empty range (start === end)
  if (visibleStart > visibleEnd) {
    return { start: requestedStart, end: requestedStart };
  }

  return { start: visibleStart, end: visibleEnd };
}
